import React, { useRef, useState } from 'react'
import { Button, Col, Overlay, Popover, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import Datetime from 'react-datetime'
import 'react-datetime/css/react-datetime.css'

// Formats a startOn/endOn param (a moment object from react-datetime, or a
// raw string before it parses) into MM/DD/YYYY for the trigger label.
const formatValue = (value) => {
  if (!value) return ''
  if (typeof value.format === 'function') return value.format('MM/DD/YYYY')
  return String(value)
}

const datePattern = '(0?[1-9]|1[012])\\/(0?[1-9]|[12][0-9]|3[01])\\/\\d{4}'
const completeDate = new RegExp(`^${datePattern}$`)

// Edits a draft of the range that only reaches the query on Apply; closing the
// popover any other way discards it.
function DateRange({ params, setParams }) {
  const [show, setShow] = useState(false)
  const [draft, setDraft] = useState({ startOn: '', endOn: '' })
  const [incomplete, setIncomplete] = useState({})
  const [resetKey, setResetKey] = useState(0)
  const target = useRef(null)
  const typedText = useRef(null)

  const start = formatValue(params.startOn)
  const end = formatValue(params.endOn)
  const filterApplied = Boolean(start || end)
  const draftSet = Boolean(draft.startOn || draft.endOn)
  const canApply = !incomplete.startOn && !incomplete.endOn

  const label = () => {
    if (start && end) return `${start} - ${end}`
    if (start) return `From ${start}`
    if (end) return `Until ${end}`
    return 'Date Range'
  }

  const open = () => {
    setDraft({ startOn: params.startOn, endOn: params.endOn })
    setIncomplete({})
    setShow(true)
  }

  // Root close stays armed through the fade-out, so a trigger click that reopens
  // the popover would otherwise close it again; the trigger toggles it itself.
  const hide = (e) => {
    if (e?.type === 'click' && target.current?.contains(e.target)) return
    setShow(false)
  }

  // react-datetime parses loosely ("07" is July 1st) and hands back raw text for
  // anything it can't parse, so typed text only counts once it is a full date.
  const updateDraft = (key) => (value) => {
    const typed = typedText.current
    typedText.current = null
    const partial =
      (typed && !completeDate.test(typed)) ||
      (value && typeof value.format !== 'function')

    setIncomplete((prev) => ({ ...prev, [key]: Boolean(partial) }))
    if (!partial) setDraft((prev) => ({ ...prev, [key]: value }))
  }

  const rememberTyped = (e) => {
    typedText.current = e.target.value
  }

  const apply = () => {
    setParams({ ...params, ...draft })
    setShow(false)
  }

  const clear = () => {
    setDraft({ startOn: '', endOn: '' })
    setIncomplete({})
    setResetKey((key) => key + 1)
    setParams({ ...params, startOn: '', endOn: '' })
  }

  const dateInput = (key, placeholder, id) => (
    <Datetime
      key={`${id}-${resetKey}`}
      inputProps={{
        placeholder,
        pattern: datePattern,
        className: 'form-control',
        id,
        onChange: rememberTyped
      }}
      dateFormat="MM/DD/YYYY"
      timeFormat={false}
      onChange={updateDraft(key)}
      initialValue={draft[key] || ''}
    />
  )

  return (
    <>
      <Button
        ref={target}
        variant={filterApplied ? 'outline-primary' : ''}
        className={`w-100 text-start ${
          filterApplied ? 'bg-white text-primary' : 'bg-white text-info'
        }`}
        style={{ border: filterApplied ? '' : '1px solid #ced4da' }}
        onClick={() => (show ? setShow(false) : open())}
        data-testid="date-range-trigger"
      >
        {label()}
      </Button>
      <Overlay
        target={target.current}
        show={show}
        placement="bottom"
        rootClose
        onHide={hide}
      >
        <Popover style={{ minWidth: '380px' }}>
          <Popover.Header>
            <Row>
              <Col sm={6} className="d-grid">
                {filterApplied || draftSet ? (
                  <Button
                    variant="link"
                    size="sm"
                    className="float-start mt-1"
                    onClick={clear}
                  >
                    <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                    Clear
                  </Button>
                ) : null}
              </Col>
              <Col sm={6} className="d-grid">
                <Button
                  variant="primary"
                  size="sm"
                  className="float-end"
                  disabled={!canApply}
                  onClick={apply}
                >
                  Apply
                </Button>
              </Col>
            </Row>
          </Popover.Header>
          <Popover.Body>
            <Row className="g-2">
              <Col>
                <label className="form-label small mb-1">Start Date</label>
                {dateInput('startOn', 'Start Date', 'start-on')}
              </Col>
              <Col>
                <label className="form-label small mb-1">End Date</label>
                {dateInput('endOn', 'End Date', 'end-on')}
              </Col>
            </Row>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  )
}

export default DateRange
